import React, { useState, useCallback } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import { setTenantOverride, getBranding, getBrandingLogoUrl, getDashboardSummary } from "../api";
import AppNav from "../components/AppNav";
import SuperAdminPage from "./SuperAdminPage";
import DashboardPage from "./DashboardPage";
import EmployeesPage from "./EmployeesPage";
import JobsPage from "./JobsPage";
import JobDetailPage from "./JobDetailPage";
import NewJobPage from "./NewJobPage";
import CandidatesPage from "./CandidatesPage";
import LeaveManagementPage from "./LeaveManagementPage";
import EmployeePortal from "./EmployeePortal";
import ExpensesPage from "./ExpensesPage";
import SalarySlipsPage from "./SalarySlipsPage";
import InvoicesPage from "./InvoicesPage";
import SettingsPage from "./SettingsPage";
import TenantPage from "./TenantPage";
// Targets of the People dashboard's tiles and quick actions. This shell renders
// DashboardPage at "/", and after the People redesign that page links to the
// Core HR pages below — without these routes those tiles dead-end here while
// working in every other shell. Routes only, deliberately not nav links: the
// override console's sidebar stays the short list it was.
// Spec: .claude/specs/people-dashboard.md
import AttendancePage from "./AttendancePage";
import AnnouncementsPage from "./AnnouncementsPage";
import HelpdeskPage from "./HelpdeskPage";
import LettersPage from "./LettersPage";
import PulsePage from "./PulsePage";
import ExitPage from "./ExitPage";
import OrgChartPage from "./OrgChartPage";

const NAVY = "#1e3a8a";

function NavLogoMark({ logoUrl, companyName }: { logoUrl: string; companyName: string }) {
  const [hasLogo, setHasLogo] = React.useState(false);
  React.useEffect(() => {
    const img = new Image();
    img.onload = () => setHasLogo(true);
    img.onerror = () => setHasLogo(false);
    img.src = logoUrl;
  }, [logoUrl]);
  if (hasLogo) {
    return <img src={logoUrl} alt={companyName} style={{ height: 32, maxWidth: 80, objectFit: "contain", borderRadius: 4 }} />;
  }
  return (
    <div style={{
      width: 32, height: 32, background: "linear-gradient(135deg, #00c9a7, #1e3a8a)",
      borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: 16, fontWeight: "bold", color: "white",
    }}>{companyName[0]?.toUpperCase() || "S"}</div>
  );
}

export default function SuperAdminShell() {
  const { user, logout } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [overrideTenant, setOverrideTenant] = useState<{ id: number; name: string } | null>(null);

  const { data: branding } = useQuery({
    queryKey: ["branding"],
    queryFn: getBranding,
    enabled: !!overrideTenant,
    staleTime: 0,
  });

  const { data: summary } = useQuery({
    queryKey: ["dashboard-summary"],
    queryFn: getDashboardSummary,
    enabled: !!overrideTenant,
    refetchInterval: 60_000,
  });

  const companyName: string = overrideTenant ? (branding?.company_name || overrideTenant.name) : "Super Admin";
  const logoUrl = getBrandingLogoUrl();

  const enterTenant = useCallback((tenant: { id: number; name: string }) => {
    setTenantOverride(tenant.id);
    qc.clear();
    setOverrideTenant(tenant);
    navigate("/");
  }, [qc, navigate]);

  const exitTenant = useCallback(() => {
    setTenantOverride(null);
    qc.clear();
    setOverrideTenant(null);
    navigate("/");
  }, [qc, navigate]);

  if (!overrideTenant) {
    const links = [{ to: "/", label: "Tenants", end: true }];
    return (
      <div style={{ minHeight: "100vh", background: "#f8fafc", fontFamily: "var(--font-sans)" }}>
        <AppNav links={links} user={user} logout={logout} companyName="Super Admin" logoUrl="" LogoMark={NavLogoMark} />
        <main className="shell-main" style={{ padding: "32px 24px" }}>
          <Routes>
            <Route path="/*" element={<SuperAdminPage onEnterTenant={enterTenant} />} />
          </Routes>
        </main>
      </div>
    );
  }

  const links = [
    { to: "/", label: "Dashboard", end: true },
    { to: "/jobs", label: "Jobs" },
    { to: "/candidates", label: "Candidates" },
    { to: "/leaves", label: "Leaves" },
    { to: "/employees", label: "Employees" },
    { to: "/expenses", label: "Expenses" },
    { to: "/payroll", label: "Payroll" },
    { to: "/invoices", label: "Invoices" },
    { to: "/settings", label: "Settings" },
    { to: "/workspace", label: "Workspace" },
  ];

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc", fontFamily: "var(--font-sans)" }}>
      {/* Override banner — fixed strip above main content, right of the sidebar */}
      <div style={{
        position: "fixed", top: 0, left: 220, right: 0, zIndex: 250,
        background: `linear-gradient(90deg, ${NAVY}, #00c9a7)`,
        color: "white", padding: "6px 20px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        fontSize: 13, fontWeight: 500, height: 36,
      }}>
        <span>👁 Viewing workspace: <strong>{overrideTenant.name}</strong></span>
        <button onClick={exitTenant} style={{
          background: "rgba(255,255,255,0.2)", color: "white",
          border: "1px solid rgba(255,255,255,0.4)", padding: "3px 12px",
          borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600,
        }}>Exit Workspace</button>
      </div>
      <AppNav links={links} user={user} logout={logout} companyName={companyName} logoUrl={logoUrl} LogoMark={NavLogoMark} />
      {/* shell-main gives margin-left: 224px; top padding accounts for the 36px banner */}
      <main className="shell-main" style={{ padding: "68px 32px 32px", overflowX: "hidden" }}>
        <Routes>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/jobs" element={<JobsPage />} />
          <Route path="/jobs/new" element={<NewJobPage />} />
          <Route path="/jobs/:id" element={<JobDetailPage />} />
          <Route path="/candidates" element={<CandidatesPage />} />
          <Route path="/leaves" element={<LeaveManagementPage />} />
          <Route path="/my-leaves" element={<EmployeePortal />} />
          <Route path="/employees" element={<EmployeesPage />} />
          <Route path="/attendance" element={<AttendancePage />} />
          <Route path="/announcements" element={<AnnouncementsPage />} />
          <Route path="/helpdesk" element={<HelpdeskPage />} />
          <Route path="/letters" element={<LettersPage />} />
          <Route path="/pulse" element={<PulsePage />} />
          <Route path="/exit" element={<ExitPage />} />
          <Route path="/org-chart" element={<OrgChartPage />} />
          <Route path="/expenses" element={<ExpensesPage />} />
          <Route path="/payroll" element={<SalarySlipsPage />} />
          <Route path="/invoices" element={<InvoicesPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/workspace" element={<TenantPage />} />
        </Routes>
      </main>
    </div>
  );
}
